export interface AccountSnapshot {
  account_id?: string
  token_id?: number
  revision?: number
  stream_epoch?: string
  broker_observed_at?: number | string | null
  positions_observed_at?: number | string | null
  orders_observed_at?: number | string | null
  positions_complete?: boolean
  orders_complete?: boolean
  positions?: unknown[]
  orders?: unknown[]
  errors?: Record<string, string>
}

export function observationMs(value: unknown): number {
  if (value == null || value === '') return 0
  const n = Number(value)
  if (Number.isFinite(n)) return n < 1e12 ? n * 1000 : n
  const parsed = Date.parse(String(value))
  return Number.isFinite(parsed) ? parsed : 0
}

export const SNAPSHOT_HEALTH_TICK_MS = 200
const GREEN_OBSERVATION_LIMIT_MS = 14500 // Retire green before the 15s broker-evidence deadline.

export function snapshotHealthy(data: AccountSnapshot | undefined, now = Date.now()): boolean {
  if (!data || data.positions_complete !== true || data.orders_complete !== true) return false
  // Aggregate/frame time cannot supply evidence missing for either resource.
  return [data.broker_observed_at, data.positions_observed_at, data.orders_observed_at]
    .map(observationMs).every(observed => observed > 0 && now - observed < GREEN_OBSERVATION_LIMIT_MS && now - observed >= -5000)
}

/** One version gate shared by REST and WS. Partial failure preserves old rows. */
export function chooseSnapshot<T extends AccountSnapshot>(current: T | undefined, incoming: T): T {
  if (!current) return incoming
  if (String(current.account_id ?? current.token_id) !== String(incoming.account_id ?? incoming.token_id)) return incoming
  if (current.revision && !incoming.revision) return current
  const resources = ['positions', 'orders'] as const
  if (resources.some(kind => incoming[`${kind}_complete`] === true &&
      observationMs(incoming[`${kind}_observed_at`]) > 0 &&
      observationMs(incoming[`${kind}_observed_at`]) < observationMs(current[`${kind}_observed_at`]))) return current
  if (current.stream_epoch === incoming.stream_epoch) {
    if ((incoming.revision ?? 0) < (current.revision ?? 0)) return current
    if (current.revision != null && incoming.revision === current.revision &&
        incoming.positions_complete === current.positions_complete &&
        incoming.orders_complete === current.orders_complete) return current
  } else {
    const hasResourceTimes = resources.some(kind => observationMs(current[`${kind}_observed_at`]) > 0)
    const advanced = hasResourceTimes
      ? resources.some(kind => observationMs(incoming[`${kind}_observed_at`]) > observationMs(current[`${kind}_observed_at`]))
      : observationMs(incoming.broker_observed_at) > observationMs(current.broker_observed_at)
    if (!advanced || incoming.positions_complete !== true || incoming.orders_complete !== true) return current
  }
  return {
    ...incoming,
    positions: incoming.positions_complete === false ? current.positions : incoming.positions,
    orders: incoming.orders_complete === false ? current.orders : incoming.orders,
    positions_observed_at: incoming.positions_complete === false ? current.positions_observed_at : incoming.positions_observed_at,
    orders_observed_at: incoming.orders_complete === false ? current.orders_observed_at : incoming.orders_observed_at,
  }
}
